import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ReactElement } from "react";
import { ContentHeader, Field, Modal, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type { CnabFileDetail, CnabFileView, CnabSettingsView, FinancialAccountView } from "@cx/contracts";
import { formatBRL, formatDateBR } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { downloadApiFile } from "../../download";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { BANK_STATUS_LABEL, BOLETO_BADGE, RETURN_ITEM_BADGE, returnSummary } from "./labels";

const dateTime = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

type SettingsForm = {
  agreement: string;
  wallet: string;
  walletVariation: string;
  nextOurNumber: string;
  nextFileSequence: string;
  beneficiaryName: string;
};

/**
 * Remessas e retornos (T2.10, RF-FIN-04): carteira de cobrança da conta (Banco do Brasil, CNAB 240), gerar a remessa
 * com os boletos pendentes e os pedidos de baixa, importar o retorno do banco (baixa automática, uma vez só) e, fora de
 * produção, simular o retorno.
 */
export function CnabPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const canCreate = can(perms, "cnab", "create");
  const canUpdate = can(perms, "cnab", "update");
  const canImport = canUpdate && can(perms, "receivables", "update");
  const qc = useQueryClient();
  const toast = useToast();
  const [accountId, setAccountId] = useState("");
  const [viewing, setViewing] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const banks = useQuery({
    queryKey: ["fin-accounts", companyId, "list"],
    enabled: can(perms, "financial_accounts", "read"),
    queryFn: () => api<{ data: FinancialAccountView[] }>("/financial-accounts"),
  });
  const cnab = useQuery({
    queryKey: ["cnab-accounts", companyId],
    queryFn: () => api<{ data: CnabSettingsView[]; sandboxAllowed: boolean }>("/cnab/accounts"),
  });
  const options = (() => {
    const fromBanks = (banks.data?.data ?? [])
      .filter((a) => a.kind === "bank" && a.active)
      .map((a) => ({ id: a.id, name: a.name }));
    const configured = (cnab.data?.data ?? []).map((a) => ({
      id: a.financialAccountId,
      name: a.accountName,
    }));
    const seen = new Set<string>();
    return [...configured, ...fromBanks].filter((a) => !seen.has(a.id) && !!seen.add(a.id));
  })();
  useEffect(() => {
    if (!accountId && options.length) setAccountId(options[0]!.id);
  }, [accountId, options]);
  const files = useQuery({
    queryKey: ["cnab-files", companyId, accountId],
    enabled: !!accountId,
    queryFn: () => api<{ data: CnabFileView[] }>(`/cnab/files?financialAccountId=${accountId}`),
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["cnab-files", companyId] });
    qc.invalidateQueries({ queryKey: ["cnab-accounts", companyId] });
    qc.invalidateQueries({ queryKey: ["cnab-settings", companyId] });
    qc.invalidateQueries({ queryKey: ["titles", companyId] });
    qc.invalidateQueries({ queryKey: ["boletos", companyId] });
    qc.invalidateQueries({ queryKey: ["fin-accounts", companyId] });
  };
  const remittance = useMutation({
    mutationFn: async () => {
      const f = await api<CnabFileView>("/cnab/remittances", {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({ financialAccountId: accountId }),
      });
      await downloadApiFile(`/cnab/files/${f.id}/download`, companyId, f.fileName);
      return f;
    },
    onSuccess: (f) => {
      toast(
        "success",
        `Remessa nº ${f.sequence} gerada com ${f.itemsCount} ${f.itemsCount === 1 ? "boleto" : "boletos"}`,
      );
      refresh();
    },
    onError: (e) => {
      toast("danger", errorMessage(e));
      refresh();
    },
  });
  const upload = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("financialAccountId", accountId);
      fd.append("file", file);
      return api<CnabFileView>("/cnab/returns", {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: fd,
      });
    },
    onSuccess: (f) => {
      toast(f.review ? "warning" : "success", `Retorno importado: ${returnSummary(f)}`);
      refresh();
      setViewing(f.id);
    },
    onError: (e) => toast("danger", errorMessage(e)),
    onSettled: () => {
      if (fileRef.current) fileRef.current.value = "";
    },
  });
  const simulate = useMutation({
    mutationFn: () =>
      downloadApiFile("/cnab/returns/simulate", companyId, "retorno-simulado.ret", {
        financialAccountId: accountId,
      }),
    onSuccess: (name) =>
      toast("success", `Retorno simulado baixado (${name}). Importe-o em "Importar retorno".`),
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const list = files.data?.data ?? [];

  return (
    <>
      <ContentHeader title="Remessas e retornos" breadcrumb={["Finanças", "Remessas e retornos"]} />
      <QueryStatus loading={cnab.isLoading} error={cnab.error} />
      <div className="box card p-3 mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-md-5">
            <label className="form-label" htmlFor="cnab-account">
              Conta de cobrança
            </label>
            <select
              id="cnab-account"
              className="form-select"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
            >
              {options.length === 0 && <option value="">Nenhuma conta bancária cadastrada</option>}
              {options.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-7 d-flex flex-wrap gap-2 justify-content-md-end">
            {canCreate && (
              <button
                type="button"
                className="btn btn-primary"
                disabled={!accountId || remittance.isPending}
                onClick={() => remittance.mutate()}
              >
                Gerar remessa
              </button>
            )}
            {canImport && (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".ret,.txt,.RET,.TXT,text/plain"
                  className="d-none"
                  aria-label="Arquivo de retorno"
                  data-testid="cnab-return-file"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) upload.mutate(f);
                  }}
                />
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  disabled={!accountId || upload.isPending}
                  onClick={() => fileRef.current?.click()}
                >
                  Importar retorno
                </button>
                {cnab.data?.sandboxAllowed && (
                  <button
                    type="button"
                    className="btn btn-outline-success"
                    disabled={!accountId || simulate.isPending}
                    onClick={() => simulate.mutate()}
                  >
                    Simular retorno
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {accountId && <SettingsCard accountId={accountId} canUpdate={canUpdate} onSaved={refresh} />}
      <div className="box card">
        <h2 className="h6 p-3 mb-0">Arquivos</h2>
        <QueryStatus loading={files.isLoading} error={files.error} />
        {files.isSuccess && list.length === 0 && (
          <p className="text-muted px-3 pb-3 mb-0">Nenhuma remessa ou retorno nesta conta.</p>
        )}
        {list.length > 0 && (
          <div className="table-responsive">
            <table className="table align-middle mb-0" aria-label="Remessas e retornos">
              <thead>
                <tr>
                  <th scope="col">Data</th>
                  <th scope="col">Tipo</th>
                  <th scope="col">Nº</th>
                  <th scope="col">Arquivo</th>
                  <th scope="col" className="text-end">
                    Boletos/ocorrências
                  </th>
                  <th scope="col" className="text-end">
                    Valor
                  </th>
                  <th scope="col">Situação</th>
                  <th scope="col">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((f) => (
                  <tr key={f.id}>
                    <td>{dateTime(f.createdAt)}</td>
                    <td>{f.direction === "remittance" ? "Remessa" : "Retorno"}</td>
                    <td>{f.sequence}</td>
                    <td className="text-break">{f.fileName}</td>
                    <td className="text-end">{f.itemsCount}</td>
                    <td className="text-end">{formatBRL(f.totalCents)}</td>
                    <td>
                      {f.direction === "return" ? (
                        <span className={f.review ? "text-warning-emphasis" : undefined}>
                          {returnSummary(f)}
                        </span>
                      ) : (
                        "Gerada"
                      )}
                    </td>
                    <td className="text-end text-nowrap">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary me-1"
                        onClick={() => setViewing(f.id)}
                      >
                        Ver
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        aria-label={`Baixar ${f.fileName}`}
                        onClick={() =>
                          downloadApiFile(`/cnab/files/${f.id}/download`, companyId, f.fileName).catch(
                            (e: Error) => toast("danger", errorMessage(e)),
                          )
                        }
                      >
                        Baixar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {viewing && <FileDialog id={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}

const emptyForm = (s?: CnabSettingsView): SettingsForm => ({
  agreement: s?.agreement ?? "",
  wallet: s?.wallet ?? "17",
  walletVariation: s?.walletVariation ?? "019",
  nextOurNumber: String(s?.nextOurNumber ?? 1),
  nextFileSequence: String(s?.nextFileSequence ?? 1),
  beneficiaryName: s?.beneficiaryName ?? "",
});

function SettingsCard({
  accountId,
  canUpdate,
  onSaved,
}: {
  accountId: string;
  canUpdate: boolean;
  onSaved(): void;
}) {
  const { companyId, api } = useSession();
  const toast = useToast();
  const [v, setV] = useState<SettingsForm>(emptyForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const settings = useQuery({
    queryKey: ["cnab-settings", companyId, accountId],
    queryFn: () => api<CnabSettingsView>(`/cnab/settings/${accountId}`),
  });
  useEffect(() => {
    if (settings.data) setV(emptyForm(settings.data));
    setErrors({});
  }, [settings.data]);
  const save = useMutation({
    mutationFn: () =>
      api(`/cnab/settings/${accountId}`, {
        method: "PUT",
        body: JSON.stringify({
          ...v,
          nextOurNumber: Number(v.nextOurNumber),
          nextFileSequence: Number(v.nextFileSequence),
        }),
      }),
    onSuccess: () => {
      toast("success", "Carteira de cobrança salva");
      setErrors({});
      onSaved();
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      toast("danger", errorMessage(e));
    },
  });
  const s = settings.data;
  const set = (k: keyof SettingsForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setV({ ...v, [k]: e.target.value });
  return (
    <div className="box card p-3 mb-3">
      <h2 className="h6 mb-3">Carteira de cobrança (Banco do Brasil · CNAB 240)</h2>
      <QueryStatus loading={settings.isLoading} error={settings.error} />
      {s && s.problems.length > 0 && (
        <div className="alert alert-warning py-2" role="alert">
          <strong>Antes de gerar boletos:</strong>
          <ul className="mb-0">
            {s.problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
          <span className="small">
            Banco, agência e conta ficam no cadastro da conta (
            <Link to="/financas/caixas-e-bancos">Caixas e bancos</Link>
            ); o CNPJ, em Preferências › Dados da empresa.
          </span>
        </div>
      )}
      {s && (
        <p className="text-muted small">
          {s.accountName} · banco {s.bankCode ?? "—"} · agência {s.agency ?? "—"} · conta{" "}
          {s.accountNumber ?? "—"}
        </p>
      )}
      <fieldset disabled={!canUpdate}>
        <div className="row">
          <Field label="Convênio" required className="col-6 col-md-2" error={errors.agreement}>
            <input inputMode="numeric" maxLength={7} value={v.agreement} onChange={set("agreement")} />
          </Field>
          <Field label="Carteira" required className="col-3 col-md-1" error={errors.wallet}>
            <input inputMode="numeric" maxLength={2} value={v.wallet} onChange={set("wallet")} />
          </Field>
          <Field label="Variação" required className="col-3 col-md-2" error={errors.walletVariation}>
            <input
              inputMode="numeric"
              maxLength={3}
              value={v.walletVariation}
              onChange={set("walletVariation")}
            />
          </Field>
          <Field
            label="Próximo nosso número"
            required
            className="col-6 col-md-2"
            error={errors.nextOurNumber}
          >
            <input
              inputMode="numeric"
              maxLength={10}
              value={v.nextOurNumber}
              onChange={set("nextOurNumber")}
            />
          </Field>
          <Field
            label="Próxima remessa (nº)"
            required
            className="col-6 col-md-2"
            error={errors.nextFileSequence}
          >
            <input
              inputMode="numeric"
              maxLength={6}
              value={v.nextFileSequence}
              onChange={set("nextFileSequence")}
            />
          </Field>
          <Field label="Nome do beneficiário" className="col-md-3" error={errors.beneficiaryName}>
            <input maxLength={60} value={v.beneficiaryName} onChange={set("beneficiaryName")} />
          </Field>
        </div>
      </fieldset>
      {canUpdate && (
        <div>
          <button
            type="button"
            className="btn btn-outline-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar carteira
          </button>
        </div>
      )}
    </div>
  );
}

function FileDialog({ id, onClose }: { id: string; onClose(): void }) {
  const { companyId, api } = useSession();
  const [open, setOpen] = useState(true);
  const file = useQuery({
    queryKey: ["cnab-files", companyId, "detail", id],
    queryFn: () => api<CnabFileDetail>(`/cnab/files/${id}`),
  });
  const f = file.data;
  return (
    <Modal
      open={open}
      title={f ? `${f.direction === "remittance" ? "Remessa" : "Retorno"} nº ${f.sequence}` : "Arquivo"}
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
          Fechar
        </button>
      }
    >
      <QueryStatus loading={file.isLoading} error={file.error} />
      {f && f.direction === "return" && (
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0" aria-label="Ocorrências do retorno">
            <thead>
              <tr>
                <th scope="col">Nosso número</th>
                <th scope="col">Ocorrência</th>
                <th scope="col">Cliente</th>
                <th scope="col" className="text-end">
                  Pago
                </th>
                <th scope="col" className="text-end">
                  Tarifa
                </th>
                <th scope="col">Situação</th>
              </tr>
            </thead>
            <tbody>
              {f.items.map((i) => {
                const b = RETURN_ITEM_BADGE[i.status];
                return (
                  <tr key={i.id}>
                    <td className="font-monospace small">{i.ourNumber}</td>
                    <td>
                      {i.movementLabel}
                      {i.creditDate && (
                        <span className="text-muted small"> · crédito {formatDateBR(i.creditDate)}</span>
                      )}
                    </td>
                    <td>
                      {i.titleId ? (
                        <Link to="/financas/contas-a-receber/$id" params={{ id: i.titleId }}>
                          {i.contactName ?? "ver conta"}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="text-end">{i.paidCents ? formatBRL(i.paidCents) : "—"}</td>
                    <td className="text-end">{i.feeCents ? formatBRL(i.feeCents) : "—"}</td>
                    <td>
                      <StatusBadge tone={b.tone}>{b.label}</StatusBadge>
                      {i.reason && <div className="small text-muted">{i.reason}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {f && f.direction === "remittance" && (
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0" aria-label="Boletos da remessa">
            <thead>
              <tr>
                <th scope="col">Nosso número</th>
                <th scope="col">Vencimento</th>
                <th scope="col" className="text-end">
                  Valor
                </th>
                <th scope="col">Situação</th>
              </tr>
            </thead>
            <tbody>
              {f.boletos.map((b) => (
                <tr key={b.id}>
                  <td className="font-monospace small">
                    <Link to="/financas/contas-a-receber/$id" params={{ id: b.titleId }}>
                      {b.ourNumber}
                    </Link>
                  </td>
                  <td>{b.dueDate ? formatDateBR(b.dueDate) : "—"}</td>
                  <td className="text-end">{formatBRL(b.amountCents)}</td>
                  <td>
                    {BOLETO_BADGE[b.status].label}
                    {b.bankStatus && (
                      <span className="text-muted small"> · {BANK_STATUS_LABEL[b.bankStatus]}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
